/**
 * 契約組織の一覧 `/organizations`。
 *
 * 05-ui では `/customers` だが、DB とプランのテーブル名が `organizations` なので
 * URL も `organizations` に揃える（運営の会話で「顧客」と「組織」が混ざらないよう、
 * 画面上の日本語は「契約組織」で統一する）。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, LogIn, MoreHorizontal, PauseCircle, PlayCircle, Plus } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  CursorPagination,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  StatusBadge,
  Textarea,
  UsageBar,
  formatBytes,
  formatCount,
  formatDateTime,
  type DataTableColumn,
} from '@mail-sv/ui';
import { ORGANIZATION_STATUS, labelOf } from '@mail-sv/shared';
import { api, download } from '../lib/api.js';
import { impersonate } from '../lib/auth.js';
import { qk } from '../lib/query-keys.js';
import { formatJpy } from '../lib/format.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { hasActiveFilter } from '../lib/search.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { SUSPEND_MODE_OPTIONS, type SuspendMode } from '../lib/suspension.js';
import { authStore, useAuth, usePermission } from '../stores/auth.js';
import { FilterBar, optionsFrom } from '../components/filter-bar.js';
import { FormDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { ListEmpty, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type { CountedList, ImpersonationScope, OpsPlan, OrganizationSummary, StaffUser } from '../types/api.js';

const routeApi = getRouteApi(RID.organizations);

export function OrganizationsPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canSuspend = usePermission(OPS_PERMISSION.orgSuspend);
  const canImpersonate = usePermission(OPS_PERMISSION.impersonate);
  const canImpersonateWrite = usePermission(OPS_PERMISSION.impersonateWrite);
  const canCreate = usePermission(OPS_PERMISSION.orgWrite);

  const [suspendTarget, setSuspendTarget] = React.useState<OrganizationSummary | null>(null);
  const [impersonateTarget, setImpersonateTarget] = React.useState<OrganizationSummary | null>(null);
  const [createOpen, setCreateOpen] = React.useState(false);

  /*
   * **サーバーが解釈できるものだけを送る。**
   * `GET /ops/orgs` が受けるのは `status` / `q` / `sort` / `order` の 4 つ。
   * 以前は `subscription` / `planCode` も送っていたが、サーバーは黙って捨てるため
   * 「絞り込んだのに件数が変わらない」画面になっていた（フィルタが効かない、
   * ではなく**効いているように見えて効いていない**のが悪い）。
   */
  const list = useCursorList<OrganizationSummary>({
    path: '/orgs',
    keyFor: (params) => qk.orgs(params),
    filters: {
      q: search.q,
      status: search.status,
      sort: search.sort,
      order: search.order,
    },
  });

  const setSearch = (patch: Partial<typeof search>): void => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  };

  const columns: Array<DataTableColumn<OrganizationSummary>> = [
    {
      id: 'name',
      header: '契約組織',
      // サーバー側 keyset ソート（`sort=name|slug`）。クライアントで並べ替えると
      // 「今めくっているページの中だけが並ぶ」表になる。
      sortable: true,
      cell: (o) => (
        <div className="min-w-0">
          <Link
            to="/organizations/$orgId"
            params={{ orgId: o.id }}
            search={{ tab: 'overview' }}
            className="block truncate font-medium hover:text-primary"
          >
            {o.displayName}
          </Link>
          <span className="block truncate text-xs text-muted-foreground">{o.slug}</span>
        </div>
      ),
    },
    { id: 'plan', header: 'プラン', hideBelow: 'md', cell: (o) => o.planName },
    {
      id: 'status',
      header: '状態',
      cell: (o) => (
        <div className="flex flex-wrap gap-1">
          <StatusBadge status={o.status} size="sm" />
          <StatusBadge status={o.subscriptionStatus} size="sm" />
        </div>
      ),
    },
    { id: 'domains', header: 'ドメイン', align: 'right', hideBelow: 'lg', cell: (o) => formatCount(o.domainCount) },
    {
      id: 'mailboxes',
      header: 'メールボックス',
      align: 'right',
      hideBelow: 'md',
      cell: (o) => formatCount(o.mailboxCount),
    },
    {
      id: 'storage',
      header: '使用容量',
      width: 'w-40',
      hideBelow: 'lg',
      cell: (o) => (
        <UsageBar used={o.storageUsedBytes} total={o.storageLimitBytes} formatValue={(v) => formatBytes(v)} />
      ),
    },
    { id: 'amount', header: '月額', align: 'right', hideBelow: 'md', cell: (o) => formatJpy(o.monthlyAmountJpy) },
    { id: 'next', header: '次回請求', hideBelow: 'lg', cell: (o) => formatDateTime(o.nextInvoiceAt) },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      width: 'w-12',
      align: 'right',
      cell: (o) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label={`${o.displayName} の操作`}>
              <MoreHorizontal aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="truncate">{o.displayName}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/organizations/$orgId" params={{ orgId: o.id }} search={{ tab: 'overview' }}>
                詳細を開く
              </Link>
            </DropdownMenuItem>
            {canImpersonate ? (
              <DropdownMenuItem onSelect={() => setImpersonateTarget(o)}>
                <LogIn className="size-4" aria-hidden />
                契約者として操作する
              </DropdownMenuItem>
            ) : null}
            {canSuspend ? (
              <>
                <DropdownMenuSeparator />
                {o.status === 'suspended' ? (
                  <UnsuspendItem org={o} />
                ) : (
                  <DropdownMenuItem className="text-destructive" onSelect={() => setSuspendTarget(o)}>
                    <PauseCircle className="size-4" aria-hidden />
                    利用を停止する
                  </DropdownMenuItem>
                )}
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="契約組織"
        description="契約中のテナントと、その利用状況・請求状態。"
        actions={
          <>
            <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
            {canCreate ? (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus aria-hidden />
                契約組織を作成
              </Button>
            ) : null}
            <Button
              variant="outline"
              onClick={() =>
                void download('/orgs/export', `organizations-${new Date().toISOString().slice(0, 10)}.csv`, {
                  q: search.q,
                  status: search.status,
                })
              }
            >
              <Download aria-hidden />
              CSV エクスポート
            </Button>
          </>
        }
      />

      <FilterBar
        q={search.q}
        onQChange={(q) => setSearch({ q })}
        placeholder="組織名 / 連絡先 / ドメインで絞り込む"
        active={hasActiveFilter(search)}
        onClear={() => void navigate({ search: { sort: 'id', order: 'asc' } })}
        selects={[
          {
            key: 'status',
            label: '組織',
            value: search.status,
            options: optionsFrom(ORGANIZATION_STATUS, labelOf),
            onChange: (v) => setSearch({ status: v as typeof search.status }),
          },
        ]}
      />

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(o) => o.id}
        loading={list.query.isLoading}
        sort={{ columnId: search.sort, direction: search.order }}
        onSortChange={(next) =>
          setSearch({
            sort: next.columnId as typeof search.sort,
            order: next.direction,
          })
        }
        empty={
          <ListEmpty
            filtered={hasActiveFilter(search)}
            entityLabel="契約組織"
            onClearFilter={() => void navigate({ search: { sort: 'id', order: 'asc' } })}
          />
        }
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      <CreateOrgDialog open={createOpen} onOpenChange={setCreateOpen} />
      <SuspendDialog org={suspendTarget} onClose={() => setSuspendTarget(null)} />
      <ImpersonateDialog
        org={impersonateTarget}
        allowWritable={canImpersonateWrite}
        onClose={() => setImpersonateTarget(null)}
      />
    </>
  );
}

/* ------------------------------------------------------------------ 停止 */

function SuspendDialog({
  org,
  onClose,
}: {
  org: OrganizationSummary | null;
  onClose: () => void;
}): React.ReactElement | null {
  const qc = useQueryClient();
  const [reason, setReason] = React.useState('');
  const [mode, setMode] = React.useState<SuspendMode>('outbound');

  React.useEffect(() => {
    if (org) {
      setReason('');
      setMode('outbound');
    }
  }, [org]);

  const mut = useMutation({
    mutationFn: () => api.post(`/orgs/${org?.id ?? ''}/suspend`, { reason, mode }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.orgs() });
      onClose();
    },
  });

  if (!org) return null;

  return (
    <FormDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={`${org.displayName} の利用を停止`}
      description="既定は送信のみの停止です（支払い遅延で取引先に実害を出さないため、受信は止めません）。"
      submitLabel="停止する"
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      disabled={reason.trim().length < 5}
      onSubmit={() => mut.mutate()}
    >
      <Field
        label="停止の範囲"
        hint="サーバー側の語彙（outbound / delivery / full）と 1 対 1。選んだ範囲がそのまま適用されます。"
      >
        <NativeSelect value={mode} onChange={(v) => setMode(v as SuspendMode)} options={SUSPEND_MODE_OPTIONS} />
      </Field>
      <Field label="理由" required hint="監査ログと契約者向け通知に記録されます。5 文字以上。">
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
      </Field>
    </FormDialog>
  );
}

function UnsuspendItem({ org }: { org: OrganizationSummary }): React.ReactElement {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.post(`/orgs/${org.id}/unsuspend`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.orgs() }),
  });
  return (
    <DropdownMenuItem onSelect={() => mut.mutate()} disabled={mut.isPending}>
      <PlayCircle className="size-4" aria-hidden />
      停止を解除する
    </DropdownMenuItem>
  );
}

/* -------------------------------------------------------------- 代理ログイン */

function ImpersonateDialog({
  org,
  allowWritable,
  onClose,
}: {
  org: OrganizationSummary | null;
  allowWritable: boolean;
  onClose: () => void;
}): React.ReactElement | null {
  const qc = useQueryClient();
  const me = useAuth().user;
  const [reason, setReason] = React.useState('');
  const [scope, setScope] = React.useState<ImpersonationScope>('read_only');
  const [durationMinutes, setDurationMinutes] = React.useState('30');
  const [approvedByStaffUserId, setApprovedByStaffUserId] = React.useState('');

  React.useEffect(() => {
    if (org) {
      setReason('');
      setScope('read_only');
      setDurationMinutes('30');
      setApprovedByStaffUserId('');
    }
  }, [org]);

  /*
   * 承認者の候補。`read_write` は**別の運営者の承認が必須**で、サーバが
   * 実在・有効・`ops.impersonate.write` 保持を毎回検証する（自己承認も弾く）。
   * 候補を出さずに ID を手入力させると、まず通らない。
   */
  const canListStaff = usePermission(OPS_PERMISSION.staffRead);
  const staff = useQuery({
    queryKey: qk.staff(),
    queryFn: () => api.get<{ items: StaffUser[] }>('/staff'),
    enabled: scope === 'read_write' && canListStaff,
  });
  const approverOptions = React.useMemo(
    () =>
      (staff.data?.items ?? [])
        .filter((s) => s.status === 'active' && s.id !== me?.id)
        .map((s) => ({ value: s.id, label: `${s.displayName}（${s.email}）` })),
    [staff.data, me?.id],
  );

  const mut = useMutation({
    /*
     * **リクエストの形は `lib/auth.ts` の `impersonate()` に 1 本化する。**
     * ここは以前 `{reason, writable, ttlSec}` を直接 POST しており、
     * サーバが `.strict()` になった今は**必ず 400** で失敗する。
     */
    mutationFn: () =>
      impersonate(org?.id ?? '', reason, {
        scope,
        durationMinutes: Number(durationMinutes),
        ...(scope === 'read_write' && approvedByStaffUserId ? { approvedByStaffUserId } : {}),
      }),
    onSuccess: (res) => {
      authStore.setImpersonating({
        impersonationSessionId: res.impersonationSessionId,
        organizationId: res.organizationId,
        organizationName: org?.displayName ?? res.organizationId,
        scope: res.scope,
        expiresAt: res.expiresAt,
      });
      void qc.invalidateQueries({ queryKey: qk.impersonations() });
      // 遷移先はサーバが決める（`portalUrl`）。admin 側でオリジンや
      // フラグメント名を組み立て直すと、取り決めが 2 か所に分かれて必ずずれる。
      window.open(res.portalUrl, '_blank', 'noopener,noreferrer');
      onClose();
    },
  });

  if (!org) return null;

  const needsApprover = scope === 'read_write' && approvedByStaffUserId.length === 0;

  return (
    <FormDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={`${org.displayName} として操作する`}
      description="代理セッションは既定で読み取り専用・最大 60 分です。操作はすべて監査ログに残り、契約者にも通知されます。"
      submitLabel="契約者パネルを開く"
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      disabled={reason.trim().length < 10 || needsApprover}
      onSubmit={() => mut.mutate()}
    >
      <Field label="理由" required hint="10 文字以上。問い合わせ番号など、後から追える情報を書いてください。">
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
      </Field>

      <Field label="有効時間" hint="5〜60 分。期限が来るとサーバー側で自動的に切れます。">
        <NativeSelect
          value={durationMinutes}
          onChange={setDurationMinutes}
          options={[
            { value: '15', label: '15 分' },
            { value: '30', label: '30 分' },
            { value: '60', label: '60 分' },
          ]}
        />
      </Field>

      {allowWritable ? (
        <div className="space-y-3 rounded-lg border border-warning/40 bg-warning/10 p-3">
          <Field label="権限">
            <NativeSelect
              value={scope}
              onChange={(v) => setScope(v as ImpersonationScope)}
              options={[
                { value: 'read_only', label: '参照のみ（推奨）' },
                { value: 'read_write', label: '参照と変更（別の運営者の承認が必要）' },
              ]}
            />
          </Field>
          {scope === 'read_write' ? (
            <Field
              label="承認者"
              required
              hint="自分自身は選べません。課金操作・API トークン管理・メールボックスの本文閲覧は、変更を許可しても実行できません。"
              error={canListStaff ? undefined : '運営ユーザーの一覧を参照する権限がないため、承認者を選べません。'}
            >
              <NativeSelect
                value={approvedByStaffUserId}
                onChange={setApprovedByStaffUserId}
                disabled={!canListStaff || staff.isLoading}
                options={[{ value: '', label: '選択してください' }, ...approverOptions]}
              />
            </Field>
          ) : null}
        </div>
      ) : null}
    </FormDialog>
  );
}

/* ------------------------------------------------------------- 作成 */

/**
 * 運営者が代理で契約組織を作る。
 *
 * ## なぜ画面が要るのか
 *
 * `POST /ops/orgs` は実装済みだったが**どの画面からも呼べなかった**。
 * セルフサインアップ（`POST /public/signup`）は `LEGAL_*` が未設定だと 503 で
 * 止まるので、法定ページを整える前の環境では**契約組織を 1 つも作れない**。
 * デモ環境や、電話・書面で申し込みを受けた分の登録がここに当たる。
 *
 * ## 運営が誤解しやすい点を画面で明示する
 *
 * - **パスワードは運営が決めない。** 招待メールのリンクから本人に設定させる。
 *   運営が初期パスワードを決めて口頭で渡す運用は、`ops-mailbox-reset` で
 *   塞いだ「運営が平文を握る」形の再発になる。
 * - **利用者本人の同意証跡は残らない。** 代理作成なので当然だが、記録が
 *   無いのと「無いと書いてある」のは後から見て別物なので、API は監査へ
 *   `consentCollected: false` を明示的に書く。画面でも言う。
 * - **理由は必須。** `PATCH /orgs/:orgId` が理由を要求するのに作成が
 *   要求しないと、「いつ・誰が・なぜ作ったか」だけが監査から抜ける。
 */
export function CreateOrgDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [slug, setSlug] = React.useState('');
  const [displayName, setDisplayName] = React.useState('');
  const [billingEmail, setBillingEmail] = React.useState('');
  const [planCode, setPlanCode] = React.useState('');
  const [billingPeriod, setBillingPeriod] = React.useState('monthly');
  const [ownerEmail, setOwnerEmail] = React.useState('');
  const [ownerName, setOwnerName] = React.useState('');
  const [reason, setReason] = React.useState('');

  /*
   * **プランは API から引く**（`PLAN_CATALOG` が正で、DB へ upsert される）。
   * 画面に code を直書きすると、カタログを増減したときにここだけ古くなる。
   */
  const plans = useQuery({
    queryKey: qk.plans(),
    queryFn: () => api.get<CountedList<OpsPlan>>('/plans'),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  React.useEffect(() => {
    if (!open) return;
    setSlug('');
    setDisplayName('');
    setBillingEmail('');
    setPlanCode('');
    setBillingPeriod('monthly');
    setOwnerEmail('');
    setOwnerName('');
    setReason('');
  }, [open]);

  const planOptions = React.useMemo(
    () => (plans.data?.items ?? []).map((p) => ({ value: p.code, label: `${p.name}（${p.code}）` })),
    [plans.data],
  );

  // 一覧が来たら先頭を既定にする（毎回選ばせる必要は無い）。
  React.useEffect(() => {
    if (planCode === '' && planOptions.length > 0) setPlanCode(planOptions[0]!.value);
  }, [planCode, planOptions]);

  const submit = useMutation({
    mutationFn: () =>
      api.post<{ id: string; ownerInviteSent: boolean }>('/orgs', {
        slug: slug.trim(),
        displayName: displayName.trim(),
        billingEmail: billingEmail.trim(),
        planCode,
        billingPeriod,
        reason: reason.trim(),
        // **`.strict()` なので空文字を送らない。** 担当者を省く運用
        // （先に器だけ作る）があるので、両方揃ったときだけ載せる。
        ...(ownerEmail.trim() && ownerName.trim()
          ? { ownerEmail: ownerEmail.trim(), ownerName: ownerName.trim() }
          : {}),
      }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: qk.orgs() });
      /*
       * **招待メールが送れなかったことを黙って閉じない。**
       *
       * `POST /ops/orgs` は招待メールを best-effort で送り、失敗しても
       * 201 を返して `ownerInviteSent: false` を載せる（組織の作成ごと
       * 巻き戻すと、再送で回復できる障害が「毎回 500 で作れない」に
       * 化けるため。これは API 側の判断として正しい）。
       *
       * だが**画面がそれを見ないと、運営には成功としか見えない**。
       * 担当者は招待リンクを一生受け取らず、リンクは Redis に sha256 しか
       * 残らないので**復元もできない**。実際に AWS のデモ環境では
       * `NOTIFY_SMTP_HOST` が未起動の自ノードを指していて送信が全滅しており、
       * この分岐が常に成立する状態だった。
       *
       * 送れなかったときはダイアログを開いたまま結果を出す。組織は既に
       * 出来ているので、閉じてよいかは運営に決めさせる。
       */
      if (askedForOwner && !res.ownerInviteSent) return;
      onOpenChange(false);
    },
  });

  /** 担当者を指定して作ろうとしたか（招待メールの成否を見るべきか）。 */
  const askedForOwner = Boolean(ownerEmail.trim() && ownerName.trim());
  /** 組織は出来たが招待メールだけ送れなかった状態。 */
  const inviteFailed = submit.isSuccess && askedForOwner && submit.data?.ownerInviteSent === false;

  // サーバー側の zod と同じ条件で先に止める（3〜34 文字・英小文字/数字/ハイフン）。
  const slugOk = /^[a-z0-9][a-z0-9-]{1,32}[a-z0-9]$/.test(slug.trim());
  // 片方だけ入れた状態を作らせない（API も 400 で弾く）。
  const ownerHalf = Boolean(ownerEmail.trim()) !== Boolean(ownerName.trim());
  const invalid =
    !slugOk ||
    displayName.trim().length === 0 ||
    billingEmail.trim().length === 0 ||
    planCode.length === 0 ||
    reason.trim().length < 5 ||
    ownerHalf;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="契約組織を作成する"
      description="運営者が代理で登録します。識別子は作成後に変更できません。"
      submitLabel={inviteFailed ? '閉じる' : '作成する'}
      pending={submit.isPending}
      error={submit.error}
      // 作成は済んでいるので、二度押しで同じ slug を作りに行かせない。
      disabled={inviteFailed ? false : invalid}
      onSubmit={() => (inviteFailed ? onOpenChange(false) : submit.mutate())}
      wide
    >
      {inviteFailed ? (
        <Alert variant="destructive">
          <AlertDescription>
            <strong>契約組織は作成できましたが、担当者への招待メールを送信できませんでした。</strong>
            このままでは担当者はパスワードを設定できず、契約者パネルにログインできません。
            招待リンクは再表示できないため、
            <strong>メール送信の設定（NOTIFY_SMTP_*）を直したうえで、 改めて招待し直す必要があります</strong>。
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="warning">
          <AlertDescription>
            代理作成では<strong>利用者本人の同意証跡が残りません</strong>。申込書やメールなど、
            同意を示すものを別途保管してください。パスワードは運営者が設定できません（担当者を
            指定すると招待メールが届き、本人が設定します）。
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field
          label="識別子（slug）"
          required
          hint="3〜34 文字の英小文字・数字・ハイフン。契約者パネルの URL に出ます。"
          error={slug.length > 0 && !slugOk ? '使用できない文字か長さです' : undefined}
        >
          <Input
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            placeholder="demo-corp"
            spellCheck={false}
          />
        </Field>
        <Field label="組織名" required>
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="デモ株式会社" />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="請求先メールアドレス" required hint="請求書と重要なお知らせの宛先。">
          <Input
            type="email"
            value={billingEmail}
            onChange={(e) => setBillingEmail(e.target.value)}
            placeholder="billing@example.jp"
            spellCheck={false}
          />
        </Field>
        <Field label="請求サイクル" required>
          <NativeSelect
            value={billingPeriod}
            onChange={setBillingPeriod}
            options={[
              { value: 'monthly', label: '月次' },
              { value: 'yearly', label: '年次' },
            ]}
          />
        </Field>
      </div>

      <Field
        label="プラン"
        required
        hint={plans.isError ? 'プランを読み込めませんでした。閉じてやり直してください。' : undefined}
      >
        <NativeSelect value={planCode} onChange={setPlanCode} options={planOptions} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="担当者のメールアドレス" hint="省略できます（先に器だけ作るとき）。">
          <Input
            type="email"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            placeholder="owner@example.jp"
            spellCheck={false}
          />
        </Field>
        <Field label="担当者の氏名" error={ownerHalf ? 'メールアドレスと氏名は両方入力してください' : undefined}>
          <Input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="山田 太郎" />
        </Field>
      </div>

      <Field label="作成する理由" required hint="監査ログに残ります（5 文字以上）。">
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="電話で申し込みを受けたため代理登録"
        />
      </Field>
    </FormDialog>
  );
}
